// src/pages/Enseignants/Attendance/components/AttendanceFiltersPanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaExchangeAlt,
  FaFilter,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrashAlt,
  FaUserCheck,
  FaUserInjured,
  FaUserTimes,
} from "react-icons/fa";

import { STATUS_OPTIONS, getLocalDateInput } from "../utils/attendanceMobileUtils";

export default function AttendanceFiltersPanel({
  loading,
  filters,
  setFilters,
  seasons = [],
  dojos = [],
  isAdmin = false,
  onRefresh,
  onDeleteAll,
}) {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setLocalSearch(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((previous) => ({
        ...previous,
        search: localSearch,
      }));
    }, 250);

    return () => clearTimeout(timer);
  }, [localSearch, setFilters]);

  const hasSingleDojo = useMemo(() => dojos.length === 1, [dojos]);

  const resetFilters = () => {
    setLocalSearch("");

    setFilters((previous) => ({
      ...previous,
      search: "",
      statutInscription: "",
      selectedDate: getLocalDateInput(),
      hideStopped: true,
      sortAssiduity: false,
      onlyBirthday: false,
      onlyBlessed: false,
      onlyPresent: false,
      onlyAbsent: false,
      selectedDojo: hasSingleDojo ? previous.selectedDojo : previous.selectedDojo,
    }));
  };

  const changeGroup = () => {
    setFilters((previous) => ({
      ...previous,
      filterGroup: "",
      search: "",
      onlyPresent: false,
      onlyAbsent: false,
    }));
  };

  const fieldClass =
    "min-h-[46px] w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white";

  const labelClass =
    "mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5">
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <FaFilter />
                Appel en cours
              </p>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Groupe, date et filtres rapides pour le téléphone.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen((value) => !value)}
              className="inline-flex min-h-[42px] shrink-0 items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <FaFilter />
              Filtres
            </button>
          </div>

          <div className="rounded-[24px] border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-500/20 dark:bg-indigo-500/10">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-indigo-700/80 dark:text-indigo-200/80">
                  Groupe sélectionné
                </p>

                <h3 className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
                  {filters.filterGroup || "Aucun groupe"}
                </h3>
              </div>

              <button
                type="button"
                onClick={changeGroup}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-[#013E8E] shadow-sm ring-1 ring-indigo-200 transition hover:bg-indigo-50 dark:bg-slate-950 dark:text-indigo-300 dark:ring-indigo-500/20 dark:hover:bg-slate-900"
              >
                <FaExchangeAlt />
                Changer de groupe
              </button>
            </div>
          </div>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={localSearch}
              onChange={(event) => setLocalSearch(event.target.value)}
              disabled={loading}
              placeholder="Rechercher nom, prénom, licence..."
              className="min-h-[48px] w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1">
                  <FaCalendarAlt />
                  Date
                </span>
              </label>

              <input
                type="date"
                value={filters.selectedDate}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    selectedDate: event.target.value,
                  }))
                }
                disabled={loading}
                className={fieldClass}
              />
            </div>

            <div>
              <label className={labelClass}>Saison</label>

              <select
                value={filters.selectedSeason}
                onChange={(event) =>
                  setFilters((previous) => ({
                    ...previous,
                    selectedSeason: event.target.value,
                  }))
                }
                disabled={loading}
                className={fieldClass}
              >
                <option value="">Toutes</option>
                {seasons.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filtersOpen && (
            <div className="space-y-3 rounded-[24px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50">
              <div>
                <label className={labelClass}>Statut</label>

                <select
                  value={filters.statutInscription}
                  onChange={(event) =>
                    setFilters((previous) => ({
                      ...previous,
                      statutInscription: event.target.value,
                    }))
                  }
                  disabled={loading}
                  className={fieldClass}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status || "all"} value={status}>
                      {status || "Tous les statuts"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={loading}
                  className="inline-flex min-h-[42px] items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  <FaSyncAlt className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>

                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex min-h-[42px] items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  <FaTimes />
                  Réinitialiser
                </button>

                {isAdmin && onDeleteAll && (
                  <button
                    type="button"
                    onClick={onDeleteAll}
                    className="inline-flex min-h-[42px] items-center gap-2 rounded-2xl bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 ring-1 ring-rose-200 transition hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
                  >
                    <FaTrashAlt />
                    Supprimer appels
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto pb-1">
            <FilterChip
              active={filters.hideStopped}
              label="Masquer arrêts"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  hideStopped: !previous.hideStopped,
                }))
              }
            />

            <FilterChip
              active={filters.sortAssiduity}
              label="Tri assiduité"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  sortAssiduity: !previous.sortAssiduity,
                }))
              }
            />

            <FilterChip
              active={filters.onlyPresent}
              label="Présents"
              icon={<FaUserCheck />}
              tone="emerald"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  onlyPresent: !previous.onlyPresent,
                  onlyAbsent: false,
                }))
              }
            />

            <FilterChip
              active={filters.onlyAbsent}
              label="Absents"
              icon={<FaUserTimes />}
              tone="rose"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  onlyAbsent: !previous.onlyAbsent,
                  onlyPresent: false,
                }))
              }
            />

            <FilterChip
              active={filters.onlyBirthday}
              label="Anniversaires"
              icon={<FaBirthdayCake />}
              tone="pink"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  onlyBirthday: !previous.onlyBirthday,
                }))
              }
            />

            <FilterChip
              active={filters.onlyBlessed}
              label="Blessés"
              icon={<FaUserInjured />}
              tone="rose"
              onClick={() =>
                setFilters((previous) => ({
                  ...previous,
                  onlyBlessed: !previous.onlyBlessed,
                }))
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function FilterChip({ active, onClick, label, icon, tone = "indigo" }) {
  const activeClass =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
      : tone === "rose"
      ? "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
      : tone === "pink"
      ? "bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-900/20 dark:text-pink-200 dark:ring-pink-800/60"
      : "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex min-h-[40px] shrink-0 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold ring-1 transition ${
        active
          ? activeClass
          : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
      }`}
    >
      {icon}
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-current" : "bg-slate-400"
        }`}
      />
      {label}
    </button>
  );
}